import { Outlet, NavLink, useLocation } from "react-router-dom";
import { hasAnyRole } from "../lib/roles";
import { VehicleInvoicePopup } from "./VehicleInvoicePopup";
import { CdBookedAlertPopup } from "./CdBookedAlertPopup";
import { ShopWaitingPartsAlertPopup } from "./ShopWaitingPartsAlertPopup";
import { 
  Wrench, Stethoscope, Menu, X, Package,
  ClipboardList, ChevronDown,
  MapPin, Fuel, FileCheck, ListChecks,
  ChevronsLeft, ShieldCheck, LayoutDashboard,
  Ship, Hammer, PackageSearch, MousePointerClick,
  CalendarDays
} from "lucide-react";
import { GolfCartIcon } from "./icons/GolfCartIcon";
import { FloridaIcon } from "./icons/FloridaIcon";
import { UsaIcon } from "./icons/UsaIcon";
import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { SidebarFooter } from "./SidebarFooter";
import "../styles/operations-light.css";
import SyncFailureBanner from "./SyncFailureBanner";
import { BulletinPopup } from "./BulletinSystem";
import { prefetchMainRoutes } from "../lib/prefetchRoutes";
import axios from "axios";
import BACKEND_URL from "../lib/apiConfig";

const API = `${BACKEND_URL}/api`;

function useSlashSearch() {
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== '/') return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || document.activeElement?.isContentEditable) return;
      const input = document.querySelector('[data-search-input]');
      if (input) { e.preventDefault(); input.focus(); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);
}

const navGroups = [
  {
    label: "",
    collapsible: false,
    items: [
      { path: "/service-calls", icon: Wrench, label: "Service Dispatch" },
      // iter 379c — Jake's nav reorder/rename: All Shipping directly
      // below Service Dispatch; "Florida" → "Florida Deliveries";
      // "OOS Shipping" → "OOS Deliveries".
      { path: "/service-calls?tab=all", icon: Ship, label: "All Shipping", matchQuery: "tab=all", allowedRoles: ["admin", "dispatcher"] },
      // iter 497 — Jake: Two-week dispatch calendar directly below
      // All Shipping. Desktop-only (no role restriction beyond the
      // normal dispatcher/admin sidebar visibility).
      { path: "/schedule-calendar", icon: CalendarDays, label: "Schedule Calendar", allowedRoles: ["admin", "dispatcher"] },
      { path: "/service-calls?tab=florida", icon: FloridaIcon, label: "Florida Deliveries", matchQuery: "tab=florida", allowedRoles: ["admin", "dispatcher"] },
      { path: "/service-calls?tab=oos", icon: UsaIcon, label: "OOS Deliveries", matchQuery: "tab=oos", allowedRoles: ["admin", "dispatcher"] },
      { path: "/parts-orders", icon: PackageSearch, label: "Parts Orders" },
      { path: "/street-legal", icon: FileCheck, label: "Street Legal" },
      { path: "/warranty", icon: ShieldCheck, label: "Warranty" },
      // iter 378r — Jake: Rebuilds sits between Warranty and Parts
      // Inventory; Dashboard + Photos moved under More; Dispatch is home.
      { path: "/rebuilds", icon: Hammer, label: "Rebuilds" },
      { path: "/inventory", icon: Package, label: "Parts Inventory" },
      // iter 379f — Jake: Bulletins promoted from More to the main nav.
      // iter 381j — Jake: Diagnostics promoted from More to the main nav.
      { path: "/diagnostics", icon: Stethoscope, label: "Diagnostics" },
    ]
  },
  {
    label: "More",
    defaultCollapsed: true,
    collapsible: true,
    items: [
      { path: "/daily-dashboard", icon: LayoutDashboard, label: "Dashboard" },
      // iter 343: Jake asked for Customer Printouts to live at the top
      // of More — it's a frequent tap target for the shop.
      { path: "/build-sheet-library", icon: ClipboardList, label: "Customer Printouts", allowedRoles: ["admin", "manager", "dispatcher"] },
      // iter 370bz — Service Call Tagging — the simplified successor
      // to the old Prevention Report. Lives under "More" so it's one
      // tap away when Jake wants to chew through the untagged backlog
      // without cluttering the primary nav.
      { path: "/prevention", icon: ListChecks, label: "Service Call Tagging", allowedRoles: ["admin", "manager", "dispatcher"] },
      // iter 370kt — Cart-inventory physical audit page (mobile-first
      // search + tap-to-check workflow). Lives under "More" because
      // it's only used during audit days, not daily dispatch flows.
      { path: "/cart-audit", icon: ClipboardList, label: "Cart Audit", allowedRoles: ["admin", "manager", "dispatcher"] },
      // iter 440 — Jake: Cart Inventory (Available Inventory + Forecasting)
      // moved from the primary nav into More while the CRM takes over
      // inventory. A 2-week sunset reminder emails Jake when it's likely
      // time to retire it (services/cart_inventory_sunset.py).
      { path: "/stock-overview", icon: GolfCartIcon, label: "Cart Inventory" },
      // iter 370kt — Service-call recall checklist admin (separate
      // feature: per-customer "while you're there" recall items).
      { path: "/recall-checklist-admin", icon: ListChecks, label: "Recall Checklist", allowedRoles: ["admin", "manager", "dispatcher"] },
      // iter 427 — Team Reviews left the sidebar; reached from the Users page
      // (Settings → Users → "Team Reviews"). Route + backend allowlist unchanged.
      { path: "/location-planner", icon: MapPin, label: "Location Planner" },
      { path: "/fleet-maintenance", icon: Fuel, label: "Fleet" },
      // iter 403 — 30-day click-usage audit (admin only).
      { path: "/platform-usage", icon: MousePointerClick, label: "Platform Usage", allowedRoles: ["admin"] },
      // iter 350 — Jake removed `/sync-monitor` and `/admin/error-logs`
      // dedicated pages. Error logs now live inline on /settings
      // (Users page). Sync failures surface via `<SyncFailureBanner />`
      // at the top of the Layout when the sync-monitor API reports
      // failures.
    ]
  }
];

export default function Layout({ user, onLogout }) {
  const location = useLocation();
  // iter 385 — warm every main-nav page chunk during idle time so the
  // first click on any nav item doesn't wait on a JS download.
  useEffect(() => { prefetchMainRoutes(); }, []);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem("sidebar_collapsed") === "true";
  });
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    const defaults = {};
    navGroups.forEach(g => { if (g.defaultCollapsed) defaults[g.label] = true; });
    return defaults;
  });
  const [bulletinCount, setBulletinCount] = useState(0);
  const [backendVersion, setBackendVersion] = useState(null);

  useSlashSearch();

  // iter 373 — WS connection + cache bridge moved to <GlobalWebSocket />
  // in App.js so standalone routes (shop/detailer/mobile) get events too.

  // Fetch the backend's deployed version once on mount so we can flag
  // mismatches (e.g. stale frontend tab still loaded after a backend deploy).
  useEffect(() => {
    axios.get(`${API}/health`)
      .then(res => setBackendVersion(res.data?.version || null))
      .catch(() => {});
  }, []);

  // Fetch active (unread) bulletin count for sidebar badge
  useEffect(() => {
    const fetchCount = () => {
      axios.get(`${API}/bulletins/active-count`)
        .then(res => setBulletinCount(res.data?.count || 0))
        .catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, []);

  const toggleSidebarCollapse = () => {
    setSidebarCollapsed(prev => {
      localStorage.setItem("sidebar_collapsed", !prev ? "true" : "false");
      return !prev;
    });
  };

  const toggleGroup = (groupLabel) => {
    setCollapsedGroups(prev => ({ ...prev, [groupLabel]: !prev[groupLabel] }));
  };

  // iter 326: Children like `InventoryDashboard` render their own sticky
  // header on mobile and can't stack against the floating `top-4 left-4`
  // hamburger without visual collision. They now dispatch a
  // `app:toggle-sidebar` event and we react to it here — this keeps the
  // nav affordance inside whatever chrome each page is rendering.
  useEffect(() => {
    const onToggle = () => setSidebarOpen(prev => !prev);
    window.addEventListener("app:toggle-sidebar", onToggle);
    return () => window.removeEventListener("app:toggle-sidebar", onToggle);
  }, []);

  return (
    <div className="flex h-screen bg-[#0A0A0A]">
      {/* iter 326: /inventory renders its own sticky header with a hamburger
          that dispatches `app:toggle-sidebar`. We hide the floating Layout
          hamburger on that route so users don't see two menu buttons. */}
      {!location.pathname.startsWith("/inventory") && (
        <Button variant="ghost" size="icon" className="app-menu-toggle fixed top-4 left-4 z-[1100] lg:hidden"
          aria-label={sidebarOpen ? "Close navigation" : "Open navigation"}
          onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-menu-btn">
          {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>
      )}

      {sidebarOpen && <button aria-label="Close navigation" data-testid="sidebar-backdrop" className="fixed inset-0 bg-black/50 z-[1090] lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <aside className={`app-sidebar flex flex-col shrink-0 fixed lg:static inset-y-0 left-0 z-[1095] ${sidebarCollapsed ? 'w-[60px]' : 'w-56'} bg-[#111113] border-r border-white/5
        transform transition-all duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        data-testid="sidebar">

        <div className={`h-14 shrink-0 flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'justify-between px-4'}`}>
          <img src="https://customer-assets.emergentagent.com/job_golfcart-manager-1/artifacts/enoqxqkh_New%20Logo%20White.png"
            alt="Jake's Custom Carts" className={`${sidebarCollapsed ? 'h-7 w-auto' : 'h-10 w-auto'} transition-all`} />
          {!sidebarCollapsed && (
            <button onClick={toggleSidebarCollapse}
              className="p-1 rounded hover:bg-white/[0.06] text-zinc-500 hover:text-zinc-300 transition-colors hidden lg:flex"
              data-testid="collapse-sidebar-btn" title="Collapse sidebar">
              <ChevronsLeft className="h-4 w-4" />
            </button>
          )}
        </div>

        <nav className={`${sidebarCollapsed ? 'p-1.5' : 'p-3'} space-y-4 overflow-y-auto flex-1 min-h-0`} data-testid="main-nav">
          {navGroups.map((group) => (
            <div key={group.label || 'main'}>
              {/* Hide "More" group entirely when sidebar is collapsed */}
              {sidebarCollapsed && group.defaultCollapsed ? null : (
                <>
                  {!sidebarCollapsed && group.label && (
                    group.link ? (
                      <NavLink to={group.link}
                        className={({ isActive }) => `flex items-center justify-between w-full px-2.5 py-1.5 text-[11px] uppercase tracking-wider font-semibold transition-colors rounded-md ${
                          isActive ? "text-white bg-white/[0.08]" : "text-zinc-300 hover:text-white hover:bg-white/[0.05]"
                        }`}
                        data-testid={`nav-group-${group.label.toLowerCase()}`}>
                        {group.label}
                        {group.collapsible !== false && (
                          <ChevronDown
                            className={`h-3 w-3 transition-transform ${collapsedGroups[group.label] ? '-rotate-90' : ''}`}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleGroup(group.label); }}
                          />
                        )}
                      </NavLink>
                    ) : (
                      <button data-testid={`nav-group-${group.label.toLowerCase()}`} aria-expanded={!collapsedGroups[group.label]} onClick={() => group.collapsible !== false && toggleGroup(group.label)}
                        className="flex items-center justify-between w-full px-2 py-1 text-[11px] text-zinc-300 uppercase tracking-wider hover:text-zinc-300 transition-colors font-semibold">
                        {group.label}
                        {group.collapsible !== false && (
                          <ChevronDown className={`h-3 w-3 transition-transform ${collapsedGroups[group.label] ? '-rotate-90' : ''}`} />
                        )}
                      </button>
                    )
                  )}
                  {(sidebarCollapsed || group.collapsible === false || !collapsedGroups[group.label]) && (
                    <div className={`${sidebarCollapsed ? 'space-y-1' : 'mt-1 space-y-0.5'}`}>
                      {group.items.filter((item) => {
                        // iter 370fc — Per-user gating for sensitive
                        // pages (Team Reviews). Falls back to role
                        // gating when no `allowedUsers` list is set.
                        if (item.allowedUsers) {
                          const name = (typeof localStorage !== "undefined" && localStorage.getItem("user_name")) || "";
                          return item.allowedUsers.includes(name.trim().toLowerCase());
                        }
                        if (!item.allowedRoles) return true;
                        // iter 398 — secondary roles count (Brady: tech + dispatch)
                        return hasAnyRole(item.allowedRoles);
                      }).map((item) => {
                        // iter 370ln-C — When a sidebar entry uses a
                        // `?tab=` query param (Florida / OOS Shipping
                        // pointing at Dispatch), match on the query
                        // string too so ONLY the tab-scoped entry
                        // highlights instead of the generic "Dispatch"
                        // row also lighting up on those routes.
                        const [itemPath] = item.path.split("?");
                        const onSamePath = location.pathname === itemPath || (itemPath !== "/" && location.pathname.startsWith(itemPath));
                        const currentTab = new URLSearchParams(location.search).get("tab") || "";
                        let isActive;
                        if (item.matchQuery) {
                          // Only active when the query string matches
                          const need = new URLSearchParams(item.matchQuery).get("tab") || "";
                          isActive = onSamePath && currentTab === need;
                        } else if (itemPath === "/service-calls") {
                          // Dispatch itself: active only when NO tab
                          // (so Florida / OOS sidebar items own their
                          // own highlight without ghosting Dispatch).
                          isActive = onSamePath && !currentTab;
                        } else {
                          isActive = onSamePath;
                        }
                        return (
                          <NavLink key={item.path} to={item.path} onClick={() => setSidebarOpen(false)}
                            className={`flex items-center ${sidebarCollapsed ? 'justify-center p-2' : 'gap-2 px-3 py-2'} text-sm rounded-md transition-all duration-200 font-medium
                              ${isActive ? 'text-white bg-white/10' : 'text-white/90 hover:text-white hover:bg-white/[0.04]'}`}
                            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                            title={sidebarCollapsed ? item.label : undefined}>
                            <item.icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-white' : 'text-white/80'}`} />
                            {!sidebarCollapsed && item.label}
                            {!sidebarCollapsed && item.path === "/bulletins" && bulletinCount > 0 && (
                              <span className="ml-auto min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-[#FF3B30] text-white text-[10px] font-bold"
                                data-testid="nav-bulletins-badge">{bulletinCount}</span>
                            )}
                            {sidebarCollapsed && item.path === "/bulletins" && bulletinCount > 0 && (
                              <span className="absolute top-0 right-0 min-w-[14px] h-[14px] px-0.5 flex items-center justify-center rounded-full bg-[#FF3B30] text-white text-[8px] font-bold">{bulletinCount}</span>
                            )}
                          </NavLink>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}

        </nav>
        {user && <SidebarFooter user={user} collapsed={sidebarCollapsed} onExpand={toggleSidebarCollapse}
          onNavigate={() => setSidebarOpen(false)} onLogout={onLogout} bulletinCount={bulletinCount} backendVersion={backendVersion} />}
      </aside>

      <main className="app-main flex-1 min-w-0 overflow-auto lg:ml-0">
        {/* iter 350: top-of-main sync failure banner. Silent unless
            `/api/sync-monitor/summary` reports today_failed > 0 or
            pending_retries > 3. Jake asked to "get a message if
            something isn't syncing right" — this is that message. */}
        <SyncFailureBanner />
        <Outlet />
        <VehicleInvoicePopup />
        <CdBookedAlertPopup />
        <ShopWaitingPartsAlertPopup />
        {/* iter 421 — dispatchers/admins can now be bulletin targets (Jackson);
            give them the same acknowledge popup techs get. */}
        <BulletinPopup userId={user?.id} />
      </main>

    </div>
  );
}
